import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TableCompData,
  CardComp,
  ButtonComp,
  ModalCompAdvanceUpdate,
} from "../../../components";
import EmptyTable from "../../../components/Common/EmptyTable";
import { formatMoney } from "../../../utils";
import moment from "moment";
import { BsCalendar2Week } from "react-icons/bs";
import { convertToTitleCase } from "../../../utils/helpers";
import PageWrapper from "../modules/PageWrapper";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import MemberLoanRepaymentPayModal from "./modals/MemberLoanRepaymentPayModal";
import { installmentBalance } from "../../../components/Loan/partialRepayment";
import { describeLateFeeRule } from "../../../components/Loan/lateFee";

/** Formal (schedule-based) member loan repayment breakdown. */
const FormalMemberLoanRepaymentBreakdown = ({
  data,
  isLoading,
  loanRequestId,
}) => {
  const navigate = useNavigate();
  const [modalPage, setModalPage] = useState("");
  const [selectedRepayment, setSelectedRepayment] = useState(null);

  const lateFeeBalance = Number(data?.late_fee_balance) || 0;
  // The pay page handles part payments and late fees; the small modal only knows a plain installment
  const usePayPage = Boolean(data?.partial_repayment_enabled) || lateFeeBalance > 0;

  const useModal = [
    {
      name: "Pay Repayment",
      component: (
        <MemberLoanRepaymentPayModal
          repaymentDetails={selectedRepayment}
          loanRequestId={loanRequestId}
          setModalPage={setModalPage}
        />
      ),
    },
  ];

  const column = [
    {
      name: "S/N",
      selector: (_, index) => (
        <span className="text-muted fw-2" style={{ fontSize: "14px" }}>
          {index + 1}
        </span>
      ),
      width: "60px",
    },
    {
      name: "AMOUNT",
      selector: (row) => {
        const amount = parseFloat(row?.amount || 0);
        const roundedAmount = Math.round(amount * 100) / 100;
        return (
          <span className="fw-2 text-black1" style={{ fontSize: "14px" }}>
            {formatMoney(roundedAmount.toFixed(2))}
          </span>
        );
      },
      minWidth: "180px",
    },
    {
      name: "PAID",
      selector: (row) => (
        <span className="fw-2 text-black1" style={{ fontSize: "14px" }}>
          {formatMoney(parseFloat(row?.amount_paid || 0).toFixed(2))}
        </span>
      ),
      minWidth: "140px",
    },
    {
      name: "BALANCE",
      selector: (row) => (
        <span className="fw-2 text-black1" style={{ fontSize: "14px" }}>
          {formatMoney(installmentBalance(row).toFixed(2))}
        </span>
      ),
      minWidth: "140px",
    },
    {
      name: "DUE DATE",
      selector: (row) => {
        const selectedDateStr = row?.due_at;
        if (!selectedDateStr) return <span className="text-muted">N/A</span>;
        const selectedDate = moment(selectedDateStr, "YYYY-MM-DD");
        const today = moment().startOf("day");
        const daysDifference = selectedDate.diff(today, "days");
        const isOverdue = daysDifference < 0;
        const isDueSoon = daysDifference >= 0 && daysDifference <= 7;
        const daysText = Math.abs(daysDifference) === 1 ? "day" : "days";
        const status = row?.breakdown_status || row?.status || "";
        const showDaysBadge =
          status === "PENDING" ||
          status === "OVERDUE" ||
          status === "PARTIAL" ||
          status === "DUE" ||
          status === "";
        return (
          <div className="d-flex align-items-center gap-2">
            <BsCalendar2Week size={20} className="text-muted" />
            <span className="fw-semibold" style={{ fontSize: "14px" }}>
              {moment(row?.due_at).format("DD-MMM-YYYY")}
            </span>
            {showDaysBadge && (
              <div
                className="px-2 py-1 rounded-pill text-white small"
                style={{
                  backgroundColor: isOverdue
                    ? "#FF4343"
                    : isDueSoon
                    ? "#FFA500"
                    : "#19BC31",
                  fontSize: "11px",
                }}
              >
                {isOverdue
                  ? `${Math.abs(daysDifference)} ${daysText} overdue`
                  : isDueSoon
                  ? `Due in ${daysDifference} ${daysText}`
                  : daysDifference === 0
                  ? "Due today"
                  : `${daysDifference} ${daysText} remaining`}
              </div>
            )}
          </div>
        );
      },
      minWidth: "280px",
    },
    {
      name: "STATUS",
      selector: (row) => {
        const status = row?.breakdown_status || row?.status || "UNKNOWN";
        const statusColor =
          status === "OVERDUE"
            ? "#FF4343"
            : status === "PAID" || status === "COMPLETED"
            ? "#19BC31"
            : status === "PENDING" || status === "DUE"
            ? "#FFA500"
            : status === "PARTIAL"
            ? "#3B82F6"
            : "#A1A0A0";
        return (
          <div
            className="px-3 py-1 rounded-pill text-white text-center"
            style={{
              backgroundColor: statusColor,
              fontSize: "12px",
              minWidth: "100px",
            }}
          >
            {convertToTitleCase(status)}
          </div>
        );
      },
      width: "120px",
    },
    {
      name: "ACTION",
      cell: (row) => {
        const status = row?.breakdown_status || row?.status || "";
        const canPay =
          status === "PENDING" ||
          status === "OVERDUE" ||
          status === "PARTIAL" ||
          status === "DUE";
        return (
          <div className="text-center">
            {canPay ? (
              <ButtonComp
                onClick={() => {
                  if (usePayPage) {
                    // Part payments need the full pay page (amount, online or bank transfer)
                    navigate(
                      `/member/loan_management/loan_repayment/pay/${row.id}?loan_request_id=${loanRequestId}`
                    );
                    return;
                  }
                  setSelectedRepayment(row);
                  setModalPage("Pay Repayment");
                }}
                btnText={usePayPage ? "Pay" : "Pay Now"}
                btnClassName="bg-primary1 px-3 fw-2 h6 py-2 text-white rounded border-0"
                style={{ whiteSpace: "nowrap", minWidth: "fit-content" }}
              />
            ) : (
              <span className="text-muted small">Paid</span>
            )}
          </div>
        );
      },
      width: "150px",
    },
  ];

  return (
    <PageWrapper title="Loan Repayment Breakdown" back>
      <section className="mt-6 space-y-6">
        <CardComp cardClassName="border-0 shadow-sm">
          <div className="d-flex align-items-center gap-3 p-3">
            <div className="flex-grow-1">
              <h4 className="fw-bold mb-1 text-primary1">
                {data?.loan_type?.name || "Loan"} Repayment
              </h4>
              <p className="text-muted mb-0 small">
                View and manage your loan repayment schedule
              </p>
            </div>
          </div>
        </CardComp>

        <CardComp cardClassName="border-0 shadow-sm">
          <div className="row g-4 p-3">
            <div className="col-md-3 col-6">
              <div className="text-center">
                <p className="text-muted mb-2 small fw-semibold">Loan Tenor</p>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="text-3xl fw-bold text-dark">
                    {data?.duration || 0}
                  </span>
                  <span className="text-muted">
                    {data?.duration_type
                      ? convertToTitleCase(data.duration_type.toLowerCase())
                      : ""}
                  </span>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="text-center border-start border-end border-md-0 px-md-0 px-3">
                <p className="text-muted mb-2 small fw-semibold">Loan Interest</p>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="text-3xl fw-bold text-dark">
                    {data?.loan_type?.rate || 0}%
                  </span>
                  <span className="text-muted">Annual</span>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="text-center border-start border-end border-md-0 px-md-0 px-3">
                <p className="text-muted mb-2 small fw-semibold">
                  Overdue Repayment
                </p>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="text-3xl fw-bold text-danger">
                    {data?.overdue_repayment || 0}
                  </span>
                  <span className="text-danger fw-semibold">Overdue</span>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="text-center border-start border-end border-md-0 px-md-0 px-3">
                <p className="text-muted mb-2 small fw-semibold">
                  Pending Repayment
                </p>
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="text-3xl fw-bold text-dark">
                    {data?.pending_repayment || 0}
                  </span>
                  <span className="text-dark fw-semibold">Pending</span>
                </div>
              </div>
            </div>
          </div>
        </CardComp>

        {(data?.late_fees?.length > 0 || lateFeeBalance > 0) && (
          <CardComp cardClassName="border-0 shadow-sm">
            <div className="p-3">
              <h5 className="fw-bold mb-1 text-primary1">Late Fees</h5>
              {data?.late_fee_rule && (
                <p className="text-muted mb-2 small">{describeLateFeeRule(data.late_fee_rule)}</p>
              )}
              <p className={`mb-0 fw-semibold ${lateFeeBalance > 0 ? "text-danger" : "text-muted"}`}>
                {lateFeeBalance > 0
                  ? `You owe ${formatMoney(lateFeeBalance.toFixed(2))} in late fees. Payments clear late fees first, then the installments.`
                  : "You have no unpaid late fees."}
              </p>
            </div>
          </CardComp>
        )}

        <CardComp cardClassName="border-0 shadow-sm">
          <div className="mb-4 p-3">
            <h5 className="fw-bold mb-1 text-primary1">Repayment Breakdown</h5>
            <p className="text-muted mb-0 small">
              Detailed schedule of all repayment installments
            </p>
          </div>
          <TableCompData
            marginBottom="1rem"
            columns={column}
            data={data?.breakdowns || []}
            progressPending={isLoading}
            progressComponent={<TableSkeletonLoader rows={10} columns={5} />}
            message={
              !isLoading && (
                <EmptyTable Message="There are currently no repayment breakdowns available" />
              )
            }
            cellPadding="12px"
          />
        </CardComp>
      </section>

      <ModalCompAdvanceUpdate
        pageName={modalPage}
        arrayComp={useModal}
        handleClose={() => setModalPage()}
        centered
        size="md"
      />
    </PageWrapper>
  );
};

export default FormalMemberLoanRepaymentBreakdown;
